import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
} from '@angular/core';
import { NgIcon } from '@ng-icons/core';
import type { ContextTone } from '@quipex/shared/utils';
import {
    QpxIcon,
    QpxOverflow,
    QpxTableCell,
    QpxTooltip,
    type IconColor,
} from '../../directives';
import { Loader } from '../loader';

export type RecipientOrganisationStatusState =
    | 'loading'
    | 'recognised'
    | 'unknown'
    | 'unavailable'
    | 'failure';

@Component({
    selector: 'qpx-recipient-status, output[qpxRecipientStatus]',
    imports: [NgIcon, Loader, QpxIcon, QpxOverflow, QpxTableCell, QpxTooltip],
    templateUrl: './recipient-status.html',
    styleUrl: './recipient-status.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        'aria-live': 'polite',
        'aria-atomic': 'true',
    },
})
export class QpxRecipientStatus {
    public readonly icon = input<string>();
    public readonly iconContext = input<IconColor>();
    public readonly iconTone = input<ContextTone>();
    public readonly loading = input(false);
    public readonly loaderAriaLabel = input('Loading recipient status');

    public readonly organisationState =
        input<RecipientOrganisationStatusState>();
    public readonly organisationName = input<string>();
    public readonly organisationLabel = input('Recipient organisation');
    public readonly message = input<string>();

    protected readonly resolvedLoading = computed(
        () => this.organisationState() === 'loading' || this.loading()
    );

    protected readonly resolvedIcon = computed(() => {
        const organisationState = this.organisationState();
        if (organisationState === 'failure') return 'lucideCircleAlert';
        if (organisationState) return 'lucideBriefcaseBusiness';
        return this.icon();
    });

    protected readonly resolvedIconContext = computed<IconColor>(() =>
        this.organisationState() === 'failure'
            ? 'destructive'
            : (this.iconContext() ?? 'secondary')
    );
    protected readonly resolvedIconTone = computed(
        () =>
            this.iconTone() ??
            (this.iconContext() || this.organisationState() === 'failure'
                ? 'default'
                : 'muted')
    );
}
